import * as React from 'react';

import * as R from 'fp-ts/Record';
import * as A from 'fp-ts/Array';

import { useForm } from 'react-hook-form';
import { pipe } from 'fp-ts/lib/function';
import { useTitle } from '../../useTitle';

import { emailAddress } from '../../validators';
import { Input } from '../../components/Input';
import { SubmitButton } from '../../components/SubmitButton';
import { useAnalytics } from '../../contexts/AnalyticsContext';
import { useAuth } from '../../contexts/AuthContext';
import { useCreateAccountMutation } from '../../generated/graphql';
import { formatErrors } from '../../utils/formatErrors';
import { useStore } from '../../contexts/StoreContext';
import { LoginLayout } from '../../layouts/LoginLayout';
import { REFERRAL_CODE_KEY } from '../Referrals/referralCodeKey';
import { isNil } from '../../utils/isNil';
import { config } from '../../config';
import { SignInPrompt } from './SignInPrompt';

interface Fields {
  email: string;
  name: string;
  password: string;
  token: string;
}

export const SignUp = () => {
  useTitle('Sign up');
  const analytics = useAnalytics();
  const { refetchUser } = useAuth();
  const store = useStore();
  const {
    handleSubmit,
    setError,
    clearErrors,
    register,
    formState: { errors },
    setValue,
  } = useForm<Fields>({
    defaultValues: { email: '', password: '', name: '', token: '' },
  });

  const [turnstileWidgetId, setTurnstileWidgetId] = React.useState<
    string | null
  >(null);
  const captchaRef = React.useRef<HTMLDivElement | null>(null);
  React.useEffect(() => {
    window.turnstile.ready(() => {
      if (captchaRef.current && !turnstileWidgetId) {
        const id = window.turnstile.render(captchaRef.current, {
          sitekey: config.turnstileSiteKey,
          callback: (token) => {
            setValue('token', token);
            clearErrors('token');
          },
        });
        setTurnstileWidgetId(id);
      }
    });
  }, [captchaRef.current]);

  const [registerResponse, sendRegister] = useCreateAccountMutation();

  const submitRegistration = (values: Fields) => {
    analytics.track('Form Submitted', { form: 'register' });

    const referralCode = store.get(REFERRAL_CODE_KEY) as string;
    return sendRegister({
      input: {
        name: values.name,
        email: values.email,
        password: values.password,
        token: values.token,
        referralCode,
      },
    }).then((r) => {
      if (!isNil(r.data?.createAccount)) {
        const validationErrors = r.data?.createAccount.validation;
        if (!isNil(validationErrors)) {
          analytics.track('Data Submit Failed', {
            form: 'register',
            validation: validationErrors,
          });
          pipe(
            formatErrors(validationErrors),
            R.toEntries,
            A.map(([field, error]) => {
              if (field === 'token' && turnstileWidgetId) {
                window.turnstile.reset(turnstileWidgetId);
              }
              setError(field as keyof Fields, { message: error });
            }),
          );

          return;
        }
        if (!isNil(r.data?.createAccount.entity)) {
          // successfully registered
          analytics.track('Data Submit Succeeded', {
            form: 'register',
          });
          store.remove(REFERRAL_CODE_KEY);
          refetchUser();
        }
      }
    });
  };
  console.log(errors);
  return (
    <LoginLayout footer={<SignInPrompt />}>
      <h1 className="text-center mb-2">Create a new account</h1>

      <form onSubmit={handleSubmit(submitRegistration)} className="space-y-6">
        <div className="space-y-4">
          <Input
            label="Your name"
            type="text"
            {...register('name', { required: 'Enter your name' })}
            error={errors.name?.message}
          />
          <Input
            label="Email address"
            type="email"
            {...register('email', {
              required: 'Enter your email address',
              validate: emailAddress,
            })}
            error={errors.email?.message}
          />
          <Input
            label="Password"
            type="password"
            {...register('password', {
              required: 'Enter a password',
              minLength: { value: 8, message: 'Enter at least 8 characters' },
            })}
            error={errors.password?.message}
          />
        </div>

        <div ref={captchaRef}></div>
        {errors.token?.message ? (
          <div className="text-red-500">{errors.token?.message}</div>
        ) : null}
        <SubmitButton loading={registerResponse.fetching}>Sign up</SubmitButton>
      </form>
    </LoginLayout>
  );
};
